import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Eye,
  FileText,
  Loader2,
  Paperclip,
  Plus,
  ReceiptText,
  Stamp,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useIsReadOnly } from '@/shared/auth/SessionProvider';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { Button } from '@/shared/components/ui/button';
import { Combobox } from '@/shared/components/ui/combobox';
import { DatePicker } from '@/shared/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { ErrorDialog } from '@/shared/components/ui/status-dialog';
import { ProcessingOverlay } from '@/shared/components/ui/processing-overlay';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { uploadDocument } from '@/shared/api/documents';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { cn } from '@/shared/lib/cn';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { cajaEfectivoPara, esCajaN, etiquetaCaja } from '@/shared/lib/cajasEfectivo';
import { MoneyInput } from '@/features/ventas/components/MoneyInput';
import {
  listActiveMediosDePago,
  type MedioDePagoItem,
} from '@/shared/api/controllers/mediosDePago';
import {
  listActiveRetenciones,
  type RetencionItem,
} from '@/shared/api/controllers/retenciones';
import { getCompraDetalle } from '@/features/compras/api';
import {
  enviarPagoFacturaEmail,
  pagarCompra,
  previewRetencion,
  type PagarCompraPayload,
  type PagarInstrumentoPayload,
  type PagarRetencionPayload,
} from './api';

/**
 * PagarFacturaPage — Stage 3's main work surface. Route:
 * `/administracion/pagar/:compraId`.
 *
 * Three stacked regions:
 *   1. Compra summary (read-only)
 *   2. Instrumentos de Pago — add/remove rows; each picks Tipo (Banco
 *      or Efectivo) → Cuenta (filtered ABM_MediosDePago). Importe manual.
 *   3. Retenciones — add/remove rows; Concepto picker calls the
 *      `/retencion/preview` endpoint to auto-fill ImporteISAR + ImporteRetenido.
 *      Admin can override.
 *
 * Footer shows saldo restante, sum instrumentos, sum retenciones, and
 * the total to pay; the `Registrar pago` button is enabled only when
 * totalPago matches saldoRestante (±1¢) AND at least one instrumento is
 * present.
 *
 * On submit: server writes 08.PagosCompras + 09.InstrumentosPago +
 * 10.RetencionesPago + 11.MovimientoCajas and advances Compras.Status_CO.
 */

type InstrumentoDraft = {
  id: string;
  tipo: 'Banco' | 'Efectivo' | '';
  medio: MedioDePagoItem | null;
  importe: string;
  descripcion: string;
  /**
   * Buffered file the user picked for this instrumento (bank receipt,
   * transfer slip…). Two-step upload: we don't have the SP item id
   * until the pago commits, so the file rides along in state and gets
   * shipped to `/api/documents/upload` *after* `pagarCompra` returns
   * the created instrumento ids. Mirrors the Nueva Compra factura
   * flow. Failures degrade to a warning toast — the pago is already
   * persisted; the admin can re-upload later from the compra detail.
   */
  pendingFile?: File | null;
};

type RetencionDraft = {
  id: string;
  /**
   * SP item id when this row was seeded from an existing
   * `IDPago_RP='Pendiente'` retencion (created at Nueva Compra time).
   * The backend patches the matching row in-place when this is set,
   * so the same retencion can't be settled twice. Absent for rows
   * the user added in this Pagar Factura session.
   */
  existingId?: string;
  concepto: RetencionItem | null;
  descripcion: string;
  nroComprobante: string;
  // Sin fecha propia: la retención se practica al pagar, así que la fecha
  // de la fila es siempre la fecha de pago del header (el server la estampa).
  importeISAR: string;    // pesos
  importeRetenido: string; // pesos (editable)
  /** Last preview result's path — shown as a hint in the UI. */
  previewPath?: string;
  previewReason?: string;
  /** El admin tipeó el retenido: el recálculo automático ya no lo pisa. */
  manual?: boolean;
  /** Estado del recálculo automático; el submit espera a que no quede ninguno. */
  recalc?: 'pending' | 'error';
  /** Fila 'Pendiente': el importe estimado en Nueva Compra, para avisar si el
   *  recálculo al pagar dio distinto. */
  retenidoAlCargar?: number;
};

/**
 * Derive the broad "Tipo" category from a regime code. PowerApps
 * hardcoded two values; code `119` is Ingresos Brutos, everything else
 * is Ganancias. The server stores this literal in `Tipo_RP` for
 * reporting.
 */
function tipoFromCodigoRegimen(codigo: string | null | undefined): string {
  if ((codigo ?? '').trim() === '119') return 'Ingresos Brutos';
  return 'Ganancias';
}

/**
 * User-friendly labels for the retention engine's `path` enum (see
 * `retenciones.ts` in the backend). The raw enum leaks implementation
 * detail ("scale_general", "below_threshold"); admins want readable
 * tags that hint at which branch of the calc fired.
 */
const RETENCION_PATH_LABELS: Record<string, string> = {
  flat: 'Porcentaje fijo',
  scale_general: 'Escala general',
  scale_119: 'Régimen 119',
  below_threshold: 'No corresponde (monto no sujeto)',
  below_limite_retenciones: 'No corresponde (debajo del límite)',
  unmatched: 'Sin escala aplicable',
};

/** Engine paths that mean "no retención applies — leave at 0". */
const RETENCION_NO_CORRESPONDE_PATHS = new Set([
  'below_threshold',
  'below_limite_retenciones',
]);

export function PagarFacturaPage() {
  const { compraId } = useParams<{ compraId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isReadOnly = useIsReadOnly();
  const { config: activeCountry } = useActiveCountry();
  const showRetenciones = activeCountry.features.has('retenciones');

  const compraQ = useQuery({
    queryKey: ['compras', 'detalle', activeCountry.code, compraId] as const,
    queryFn: () => getCompraDetalle(compraId!),
    enabled: Boolean(compraId),
    staleTime: 15_000,
  });
  const mediosQ = useQuery({
    queryKey: ['medios-de-pago', 'activos', activeCountry.code] as const,
    queryFn: listActiveMediosDePago,
    staleTime: 5 * 60_000,
  });
  // Caja Diferencia is Fornitalia's internal margin bucket — only
  // Tesorería interacts with it (and via Nova auto-cascades). Never
  // selectable as a compra payment medio.
  // La gemela "Efectivo … N" tampoco: se elige la caja base y el server la
  // resuelve con el Lleva Factura de la compra (lib/cajasEfectivo).
  const payableMedios = useMemo(
    () =>
      (mediosQ.data ?? []).filter((m) => {
        const nombre = (m.fields.Medio_MDP ?? '').trim();
        return nombre !== 'Caja Diferencia' && !esCajaN(nombre);
      }),
    [mediosQ.data],
  );
  const retencionesQ = useQuery({
    queryKey: ['abm-retenciones', 'activos', activeCountry.code] as const,
    queryFn: listActiveRetenciones,
    staleTime: 5 * 60_000,
  });

  const [fechaPagoIso, setFechaPagoIso] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [tipoCambio, setTipoCambio] = useState('');
  const [instrumentos, setInstrumentos] = useState<InstrumentoDraft[]>([]);
  const [retenciones, setRetenciones] = useState<RetencionDraft[]>([]);
  // Confirmation gate — `Confirmar Pago` opens the dialog; the actual
  // mutation runs only when the user approves it. Avoids misclicks on a
  // financial action that writes 08/09/10/11 rows + advances Status_CO.
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Surfaces "comprobante is too big" errors from any instrumento row's
  // file picker. Page-level so the dialog isn't repainted per-row.
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);

  // Seed tipoCambio from the compra once it's loaded.
  useEffect(() => {
    if (!compraQ.data) return;
    const stored = compraQ.data.header.fields.TipoCambio_CO;
    if (stored && !tipoCambio) {
      setTipoCambio(String(parseMoney(stored) ?? '').replace('.', ','));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compraQ.data]);

  // Seed Pendiente retenciones — when the compra was created from
  // Nueva Compra with retenciones attached, those rows live in
  // `10.RetencionesPago` with `IDPago_RP='Pendiente'`. PowerApps surfaces
  // them in `Collect_RetencionesPagoNuevoPago` so the admin sees them
  // pre-populated and the confirm step *patches* the row (flipping
  // IDPago_RP to the real pago id) instead of duplicating it.
  //
  // Runs once when both the compra detail and the retenciones catalog
  // are available — needs the catalog to resolve the stored
  // `Concepto_RP` concat back to a `RetencionItem` for the combobox.
  // The `seededPendienteRef` guard stops a second run from clobbering
  // edits the user has already made.
  const seededPendienteRef = useRef(false);
  useEffect(() => {
    if (seededPendienteRef.current) return;
    // Retenciones are AR-only. In countries without the regime (MX) the
    // panel is hidden, so never seed Pendiente rows into state — they'd
    // otherwise ride along into the pago payload + validation invisibly.
    if (!showRetenciones) {
      seededPendienteRef.current = true;
      return;
    }
    if (!compraQ.data || !retencionesQ.data) return;
    const pendientes = compraQ.data.retenciones.filter(
      (r) => (r.fields.IDPago_RP ?? '').trim() === 'Pendiente',
    );
    if (pendientes.length === 0) {
      seededPendienteRef.current = true;
      return;
    }
    const catalog = retencionesQ.data;
    const seeded: RetencionDraft[] = pendientes.map((p) => {
      const concat = (p.fields.Concepto_RP ?? '').trim();
      const concepto =
        catalog.find(
          (c) => (c.fields.ConcatCodigoRegimenConcepto_R ?? '').trim() === concat,
        ) ?? null;
      return {
        id: crypto.randomUUID(),
        existingId: p.id,
        concepto,
        descripcion: p.fields.Descripcion_RP ?? '',
        nroComprobante: p.fields.NroComprobante_RP ?? '',
        importeISAR: formatPlain(parseMoney(p.fields.ImporteISAR_RP) ?? 0),
        importeRetenido: formatPlain(parseMoney(p.fields.ImporteRetenido_RP) ?? 0),
        retenidoAlCargar: parseMoney(p.fields.ImporteRetenido_RP) ?? 0,
      };
    });
    setRetenciones(seeded);
    seededPendienteRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compraQ.data, retencionesQ.data]);

  // --- derived values ---
  const header = compraQ.data?.header.fields;
  const compraMoneda: 'Pesos' | 'Dolares' =
    header?.Moneda_CO === 'Dolares' ? 'Dolares' : 'Pesos';
  const compraDisplayMoneda = compraMoneda === 'Dolares' ? 'USD' : 'ARS';
  const total = parseMoney(header?.Total_CO) ?? 0;
  const tcNum = parseMoney(tipoCambio) ?? 0;
  /**
   * This compra's pre-IVA total in pesos — only the initial `importeISAR` of
   * a new row. The preview replaces it with the base of THIS pago (minus what
   * earlier pagos of the compra already covered), and the server recomputes
   * it on confirm.
   */
  const compraBasePesos = useMemo(() => {
    const sub = parseMoney(header?.TotalSinIVA_CO) ?? 0;
    return compraMoneda === 'Dolares' ? sub * tcNum : sub;
  }, [header?.TotalSinIVA_CO, compraMoneda, tcNum]);
  const existingSum = useMemo(() => {
    if (!compraQ.data) return 0;
    return compraQ.data.pagos
      .filter((p) => p.fields.Status_PC !== 'Anulado')
      .reduce((s, p) => s + (parseMoney(p.fields.Importe_PC) ?? 0), 0);
  }, [compraQ.data]);
  // Pending Notas de Crédito linked to this compra. Each NC's
  // `Total_CO` is in the parent's currency (NCs inherit currency at
  // creation), so the sum can be subtracted directly without FX
  // conversion. When this pago promotes the parent to `Pagado`, the
  // server also flips these NCs to `Pagado` in the same transaction
  // — see `/api/administracion/pagar/:compraId`.
  const ncPendientesSum = useMemo(() => {
    if (!compraQ.data) return 0;
    return (compraQ.data.notasCreditoPendientes ?? []).reduce(
      (s, nc) => s + (parseMoney(nc.fields.Total_CO) ?? 0),
      0,
    );
  }, [compraQ.data]);
  const ncPendientesCount = compraQ.data?.notasCreditoPendientes?.length ?? 0;
  // `saldoPrevio` = what's still owed before the current pago
  // (Total − Σ pagos anteriores − Σ NCs pendientes). NCs are netted
  // here so the operator sees the actual cash they need to cover —
  // matches the Comprobantes de Compra + Cuentas por Pagar list math.
  const saldoPrevio = Math.max(0, total - existingSum - ncPendientesSum);

  const sumInstrumentos = instrumentos.reduce(
    (s, i) => s + (parseMoney(i.importe) ?? 0),
    0,
  );
  const sumRetenidoPesos = retenciones.reduce(
    (s, r) => s + (parseMoney(r.importeRetenido) ?? 0),
    0,
  );
  /** Retenciones apply in pesos — convert to compra currency for the saldo check. */
  const sumRetenidoCompraCurrency =
    compraMoneda === 'Dolares' && tcNum > 0
      ? sumRetenidoPesos / tcNum
      : sumRetenidoPesos;
  const totalPago = sumInstrumentos + sumRetenidoCompraCurrency;
  // QA #22: the footer's "Saldo restante" must shrink as the admin
  // adds instrumentos AND retenciones — formula `Saldo restante =
  // Total − Σ pagos anteriores − Instrumentos − Retenciones`. Earlier
  // it only subtracted prior pagos, leaving retenciones invisible to
  // the running balance.
  const saldoRestante = Math.max(0, saldoPrevio - totalPago);
  // A compra may be settled over multiple pagos — the backend stamps
  // `Status_CO = 'Pago Parcial' | 'Pagado'` accordingly. So the only hard
  // frontend rules are (a) the pago isn't empty and (b) it doesn't
  // overshoot the remaining saldo. The "fully covers" case is an
  // emphasised state, not a prerequisite.
  const fullyCovered = Math.abs(totalPago - saldoPrevio) < 0.01;
  const exceedsSaldo = totalPago - saldoPrevio > 0.01;

  /**
   * Compra-level Débito Automático flag. When SI, the bank auto-debits
   * the impuesto on its side, so we suppress the client-side preview —
   * the backend will skip posting too. PowerApps gates the same way at
   * confirm time.
   */
  const compraDA =
    (header?.DebitoAutomatico_CO ?? '').trim().toUpperCase() === 'SI';

  /**
   * Live tax preview — Ley 25.413 (Galicia 0.6%). The backend is source
   * of truth at submit and re-reads the exact rate from
   * `99.ABM_Parametros`; the rate is hardcoded here as a commonly-stable
   * constant for preview UX.
   *
   * La comisión de MercadoPago ya NO se calcula: se carga a mano desde
   * Tesorería sobre el egreso, con el botón "Agregar impuesto".
   */
  const leyRateClient = 0.006;
  const leyPreview = useMemo(() => {
    if (compraDA) return { total: 0, count: 0 };
    let total = 0;
    let count = 0;
    for (const i of instrumentos) {
      const medioName = i.medio?.fields.Medio_MDP?.trim() ?? '';
      const imp = parseMoney(i.importe) ?? 0;
      if (imp <= 0) continue;
      // Tax base is the peso amount of the movement: on a USD compra the
      // importe is USD, so the base = importe × tipo de cambio (matches the
      // backend, which keys off the compra currency, not the medio name).
      const baseArs = compraMoneda === 'Dolares' && tcNum > 0 ? imp * tcNum : imp;
      if (
        medioName === 'Transferencia Galicia' ||
        medioName === 'Transferencia Galicia Dolar'
      ) {
        total += Math.round(baseArs * leyRateClient * 100) / 100;
        count += 1;
      }
    }
    return { total, count };
  }, [instrumentos, tcNum, compraDA, compraMoneda]);

  const validationError = useMemo<string | null>(() => {
    if (!fechaPagoIso) return 'Seleccioná la fecha de pago.';
    if (!observaciones.trim()) return 'Ingresá las observaciones del pago.';
    if (compraMoneda === 'Dolares' && tcNum <= 0) return 'Ingresá el tipo de cambio.';
    if (instrumentos.length === 0) return 'Agregá al menos un instrumento de pago.';
    for (const i of instrumentos) {
      if (!i.tipo) return 'Todos los instrumentos necesitan un Tipo de cuenta.';
      if (!i.medio) return 'Todos los instrumentos necesitan una Cuenta.';
      const imp = parseMoney(i.importe) ?? 0;
      if (imp <= 0) return 'Todos los instrumentos necesitan un importe mayor a 0.';
    }
    const conceptos = retenciones.map((r) => r.concepto?.id);
    if (new Set(conceptos).size !== conceptos.length) {
      return 'Hay dos retenciones con el mismo concepto: dejá una sola.';
    }
    for (const r of retenciones) {
      if (r.recalc === 'pending') return 'Recalculando retenciones…';
      if (r.recalc === 'error') {
        return 'No se pudo recalcular una retención: cambiá la fecha para reintentar o cargá el importe a mano.';
      }
      if (!r.concepto) return 'Todas las retenciones necesitan un Concepto.';
      if (!r.nroComprobante.trim()) return 'Todas las retenciones necesitan un Nº de comprobante.';
      // Three legitimate reasons a row's retenido is 0:
      //   1. Engine flagged no-corresponde this session (PowerApps's
      //      `btn_CloseNoRetencion_CO` patches the row with
      //      `ImporteRetenido: "0"` and `Descripcion: "Sin retención"`).
      //   2. Pendiente carry-over from Nueva Compra, or Débito Automático
      //      (the bank withholds — Nueva Compra saves those at 0).
      //   3. The admin typed 0 by mistake. Only this case should block.
      const retenidoNum = parseMoney(r.importeRetenido) ?? 0;
      const noCorresponde =
        !!r.previewPath && RETENCION_NO_CORRESPONDE_PATHS.has(r.previewPath);
      if (!noCorresponde && !r.existingId && !compraDA && retenidoNum <= 0) {
        return 'Todas las retenciones necesitan un importe retenido mayor a 0.';
      }
    }
    if (totalPago <= 0) return 'El total del pago debe ser mayor a 0.';
    if (exceedsSaldo) {
      return `El total del pago (${formatMoney(totalPago, compraDisplayMoneda)}) supera el saldo restante (${formatMoney(saldoPrevio, compraDisplayMoneda)}).`;
    }
    return null;
  }, [fechaPagoIso, observaciones, compraMoneda, tcNum, instrumentos, retenciones, totalPago, exceedsSaldo, saldoPrevio, compraDisplayMoneda, compraDA]);

  // --- preview helpers ---
  // Una secuencia por fila: una respuesta vieja (fecha/TC que cambiaron, fila
  // corregida a mano) nunca pisa a una más nueva, y se escribe por id — no por
  // índice, que se corre si borran una fila con el preview en vuelo.
  const previewSeq = useRef(new Map<string, number>());
  const nextSeq = (id: string) => {
    const seq = (previewSeq.current.get(id) ?? 0) + 1;
    previewSeq.current.set(id, seq);
    return seq;
  };
  const patchRetencion = (id: string, patch: Partial<RetencionDraft>) =>
    setRetenciones((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  async function runPreviewFor(id: string, concepto: RetencionItem) {
    if (!compraId) return;
    const seq = nextSeq(id);
    patchRetencion(id, { recalc: 'pending' });
    try {
      const res = await previewRetencion({
        compraId,
        conceptoConcat: concepto.fields.ConcatCodigoRegimenConcepto_R ?? '',
        tipoCambio: tcNum > 0 ? tcNum : undefined,
        fechaPago: fechaPagoIso || undefined,
      });
      if (previewSeq.current.get(id) !== seq) return;
      if (res.outcome !== 'computed') {
        patchRetencion(id, { recalc: 'error' });
        toast.error('No se pudo calcular', { description: res.message });
        return;
      }
      const noCorresponde = RETENCION_NO_CORRESPONDE_PATHS.has(res.path);
      setRetenciones((prev) =>
        prev.map((r) =>
          r.id !== id
            ? r
            : {
                ...r,
                // Base de ESTE pago: el server descuenta lo que ya cubrieron
                // pagos anteriores de la compra (y la vuelve a calcular al
                // confirmar).
                importeISAR: formatPlain(res.base),
                importeRetenido: formatPlain(res.retenido),
                // PowerApps `btn_CloseNoRetencion_CO` overwrites the
                // user's descripcion with the literal "Sin retención"
                // when the engine flagged no-corresponde. We mirror
                // that here so the SharePoint row matches.
                descripcion: noCorresponde
                  ? 'Sin retención'
                  : r.descripcion === 'Sin retención'
                    ? ''
                    : r.descripcion,
                previewPath: res.path,
                previewReason: res.reason,
                recalc: undefined,
              },
        ),
      );
    } catch (err) {
      if (previewSeq.current.get(id) !== seq) return;
      patchRetencion(id, { recalc: 'error' });
      toast.error('Error en preview de retención', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    }
  }

  // La retención acumula los pagos del MES DEL PAGO (RG 830) y su base depende
  // del TC: con cada cambio de fecha o TC se recalculan las filas, incluidas
  // las Pendiente que Nueva Compra estimó. No se tocan las corregidas a mano
  // ni las de Débito Automático (las retiene el banco).
  const pendientesCount = retenciones.filter((r) => r.existingId).length;
  useEffect(() => {
    if (!fechaPagoIso || compraDA) return;
    if (compraMoneda === 'Dolares' && tcNum <= 0) return;
    const auto = retenciones.filter((r) => r.concepto && !r.manual);
    if (auto.length === 0) return;
    // Marcarlas ya: durante el debounce el submit no puede salir con el
    // importe viejo.
    const ids = new Set(auto.map((r) => r.id));
    setRetenciones((prev) =>
      prev.map((r) => (ids.has(r.id) ? { ...r, recalc: 'pending' } : r)),
    );
    const t = setTimeout(() => {
      for (const r of auto) void runPreviewFor(r.id, r.concepto!);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaPagoIso, tcNum, compraDA, compraMoneda, pendientesCount]);

  // --- submit ---
  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!compraId) throw new Error('Missing compraId');
      // Snapshot the current instrumentos array so the post-pago upload
      // pairs the right `pendingFile` to the right created instrumento
      // id. React state could change before the upload loop completes;
      // the snapshot keeps `instrumentos[idx] ↔ instrumentoIds[idx]`
      // alignment intact.
      const instrumentosSnapshot = instrumentos;
      const payload: PagarCompraPayload = {
        fechaPago: isoToAr(fechaPagoIso),
        observaciones: observaciones.trim() || undefined,
        tipoCambio: compraMoneda === 'Dolares' && tcNum > 0 ? tcNum : undefined,
        instrumentos: instrumentosSnapshot.map<PagarInstrumentoPayload>((i) => {
          const importe = parseMoney(i.importe) ?? 0;
          const importePesos =
            compraMoneda === 'Dolares' && tcNum > 0 ? importe * tcNum : undefined;
          return {
            medioPagoId: i.medio?.id ?? '',
            medio: i.medio?.fields.Medio_MDP ?? '',
            categoriaTipo: i.tipo as 'Banco' | 'Efectivo',
            importe,
            importePesos,
            tipoCambio: compraMoneda === 'Dolares' && tcNum > 0 ? tcNum : undefined,
            descripcion: i.descripcion.trim() || undefined,
          };
        }),
        retenciones: retenciones.map<PagarRetencionPayload>((r) => ({
          // When set, the backend patches the existing Pendiente row in
          // place (PowerApps `If(!IsBlank(LookUp('10.RetencionesPago',
          // ID = ToHandleID)))`). Absent for in-session additions.
          existingId: r.existingId,
          conceptoConcat: r.concepto?.fields.ConcatCodigoRegimenConcepto_R ?? '',
          conceptoCodigo: r.concepto?.fields.CodigoRegimen_R ?? '',
          tipo: tipoFromCodigoRegimen(r.concepto?.fields.CodigoRegimen_R),
          descripcion: r.descripcion.trim() || undefined,
          nroComprobante: r.nroComprobante.trim(),
          // Informativo: el server recalcula la base del pago al confirmar.
          importeISAR: parseMoney(r.importeISAR) ?? 0,
          importeRetenido: parseMoney(r.importeRetenido) ?? 0,
        })),
      };
      const r = await pagarCompra(compraId, payload);
      if (r.outcome !== 'paid') {
        return { res: r, fileFailures: [] as string[] };
      }
      // Two-step upload — instrumento ids only exist after the pago
      // commits. Upload each buffered file via /api/documents/upload
      // with `entityType='Instrumento'`. Failures degrade to a warning
      // toast (the pago itself is already persisted; the admin can
      // re-upload from the compra detail). Sequential rather than
      // parallel so the server isn't hit with N multipart requests at
      // once — most pagos have ≤2 instrumentos anyway.
      const fileFailures: string[] = [];
      for (let idx = 0; idx < instrumentosSnapshot.length; idx++) {
        const file = instrumentosSnapshot[idx]?.pendingFile;
        const instrumentoId = r.instrumentoIds[idx];
        if (!file || !instrumentoId) continue;
        try {
          await uploadDocument({
            entityType: 'Instrumento',
            entityId: instrumentoId,
            file,
          });
        } catch {
          fileFailures.push(file.name);
        }
      }
      // Auto-fire the "Pago de Factura" email. Server is fail-silent
      // (returns `sent:false` with a reason). We never block the pago
      // flow on the email — the most we do is surface a soft toast in
      // onSuccess so the admin knows the email didn't make it.
      let emailResult: Awaited<ReturnType<typeof enviarPagoFacturaEmail>> | null = null;
      try {
        emailResult = await enviarPagoFacturaEmail(r.pagoId);
      } catch (err) {
        console.warn('[pagar-factura] email auto-fire network error:', err);
        emailResult = { sent: false, reason: 'send_failed', error: 'network_error' };
      }
      return { res: r, fileFailures, emailResult };
    },
    onSuccess: ({ res: r, fileFailures, emailResult }) => {
      if (r.outcome === 'paid') {
        const taxNotes: string[] = [];
        if (r.leyRows > 0) taxNotes.push(`Ley 25.413: ${formatMoney(r.leyTotal, 'ARS')}`);
        // `retencion_pending` is the expected case for retención flows
        // (email fires later from cert generation) — exclude from the
        // user-facing notice.
        const emailFailed =
          Boolean(emailResult) &&
          emailResult!.sent === false &&
          emailResult!.reason !== 'retencion_pending';
        const emailReasonLabel = emailFailed
          ? emailResult!.reason === 'no_proveedor_email'
            ? 'el proveedor no tiene correo cargado'
            : emailResult!.reason === 'pdf_fetch_failed'
              ? 'no se pudo recuperar el comprobante adjunto'
              : 'falló el envío del correo'
          : '';

        // Consolidate every secondary signal into the description of a
        // single toast — the user gets one notice per operation rather
        // than a stack of overlapping toasts.
        const descriptionParts: string[] = [`Compra ahora en estado ${r.statusCO}.`];
        if (taxNotes.length > 0) descriptionParts.push(taxNotes.join(' · '));
        if (fileFailures.length > 0) {
          descriptionParts.push(
            `Comprobantes sin subir: ${fileFailures.join(', ')}. Subilos desde "Editar Compra".`,
          );
        }
        if (emailFailed) {
          descriptionParts.push(`Correo no enviado (${emailReasonLabel}).`);
        }
        if (emailResult?.sent === false && emailResult.reason === 'retencion_pending') {
          descriptionParts.push(
            'El correo al proveedor sale cuando generes el certificado de retención (Ver Compra).',
          );
        }
        const anyWarning = fileFailures.length > 0 || emailFailed;
        const description = descriptionParts.join(' ');
        if (anyWarning) {
          toast.warning('Pago registrado con advertencias', { description });
        } else {
          toast.success('Pago registrado', { description });
        }
        qc.invalidateQueries({ queryKey: ['compras'] });
        qc.invalidateQueries({ queryKey: ['administracion'] });
        // PowerApps `Screen_PagarFactura` confirm handler ends with
        // `Navigate(Screen_Administracion)` for non-admin profiles —
        // the admin's mental flow is "queue of compras to pay → pay
        // one → back to the queue". Routing through the compra detail
        // first was a detour that didn't match that mental model.
        navigate('/administracion');
      } else {
        toast.warning('No se pudo registrar', { description: r.message });
      }
    },
    onError: (err) =>
      toast.error('Error al registrar el pago', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  // --- loading / error states ---
  if (compraQ.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }
  if (compraQ.isError || !compraQ.data) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate('/administracion')} className="gap-2">
          <ArrowLeft className="h-4 w-4" aria-hidden /> Volver
        </Button>
        <div className="rounded-2xl border border-dashed bg-card/40 p-10 text-center text-sm text-muted-foreground">
          Compra no encontrada.
        </div>
      </div>
    );
  }

  const canSubmit = validationError === null && !submitMutation.isPending;

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto pb-2">
      {/* Top bar */}
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <Button
          size="icon"
          className="h-10 w-10 shrink-0 rounded-full bg-foreground text-background hover:bg-foreground/90"
          // The pago flow is reached from Administración → Cuentas por
          // Pagar; the compra detail isn't part of the path. Going back
          // to the compra forced the admin into a two-click trip back
          // to Administración. Skip the detour and jump straight to
          // /administracion. (The post-pago success handler still
          // forwards via the compra detail with `state.from` so that
          // page's own Volver returns to Administración.)
          onClick={() => navigate('/administracion')}
          aria-label="Volver a Administración"
          title="Volver a Administración"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          Pagar factura
        </h1>
      </div>

      {/* Compra summary + pago header in one card.
          Row 1 (4-col): Proveedor · Comprobante (Nº only) · Moneda · Fecha de pago
          Row 2 (USD only): Tipo de Cambio
          Row 3: Observaciones (required, full-width textarea)
          Every control shares h-12 so the rows sit on a single baseline.
          Saldo restante lives only in the footer TotalPill — keeping it
          once avoids two sources of truth drifting. */}
      <div className="space-y-3 rounded-2xl border bg-card p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Field label="Proveedor">
            <Input variant="filled" value={header?.Proveedor_CO ?? ''} readOnly disabled />
          </Field>
          {/* Only the invoice number — the Comprobante type (Factura A/B/C,
              etc.) is already implied by the compra context and adds noise
              here. */}
          <Field label="Comprobante">
            <Input
              variant="filled"
              value={header?.NumeroComprobante_CO ?? ''}
              readOnly
              disabled
            />
          </Field>
          <Field label="Moneda">
            <Input variant="filled" value={header?.Moneda_CO ?? ''} readOnly disabled />
          </Field>
          <Field label="Fecha de pago">
            <DatePicker
              value={fechaPagoIso}
              onChange={setFechaPagoIso}
              placeholder="Seleccionar…"
              className="h-12"
            />
          </Field>
        </div>
        {compraMoneda === 'Dolares' && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <Field label="Tipo de Cambio">
              <Input
                variant="filled"
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                value={tipoCambio}
                onChange={(e) => setTipoCambio(sanitizeDecimal(e.target.value))}
              />
            </Field>
          </div>
        )}
        {/* Observaciones — required (matches PowerApps DisplayMode gate). */}
        <Field
          label={
            <>
              Observaciones <span className="text-primary">*</span>
            </>
          }
        >
          <Textarea
            variant="filled"
            rows={3}
            placeholder="Ingresar Observaciones"
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
          />
        </Field>
      </div>

      {/* QA #18: Retenciones only apply to compras con factura. When
          the compra was created with "Lleva Factura: NO" the entire
          block is hidden — there's no factura to withhold against,
          so the retenciones don't have a legal basis. Mirrors the
          frontend Nueva Compra gate at the create step. Country-gated:
          MX has no retenciones flow, so the block is hidden there too. */}
      {showRetenciones &&
        (header?.LlevaFactura_CO ?? '').trim().toUpperCase() === 'SI' && (
        <>
          {/* Retenciones first — matches PowerApps Screen_PagarFactura order
              (retenciones are declared before the instruments that actually
              settle the cuenta corriente). */}
          <SectionHeader
            title="Retenciones"
            onAdd={() =>
              setRetenciones((prev) => [
                ...prev,
                {
                  id: crypto.randomUUID(),
                  concepto: null,
                  descripcion: '',
                  nroComprobante: header?.NumeroComprobante_CO ?? '',
                  // Seed with this compra's base in pesos — matches PowerApps's
                  // `lbl_AddImporteISAR_CO = Sum(lines Precio×Cantidad)`.
                  importeISAR: String(compraBasePesos).replace('.', ','),
                  importeRetenido: '',
                },
              ])
            }
          />
          {retenciones.length === 0 ? (
            <EmptyNote
              icon={<FileText className="h-5 w-5" aria-hidden />}
              title="Aun no se agregaron retenciones"
              hint="Agrega una retención para visualizarla aquí"
            />
          ) : (
            <div className="space-y-2">
              {retenciones.map((r) => (
                <RetencionRow
                  key={r.id}
                  row={r}
                  catalog={retencionesQ.data ?? []}
                  catalogLoading={retencionesQ.isPending}
                  fechaPagoIso={fechaPagoIso}
                  onChange={(patch) => {
                    // Tipear el retenido lo vuelve manual y descarta el
                    // recálculo en vuelo de esa fila.
                    if ('importeRetenido' in patch) {
                      nextSeq(r.id);
                      patch = { ...patch, manual: true, recalc: undefined };
                    }
                    patchRetencion(r.id, patch);
                  }}
                  onConceptoPicked={(concepto) => {
                    patchRetencion(r.id, { concepto, manual: false });
                    // Débito Automático: la retiene el banco, no se calcula
                    // (igual que en Nueva Compra).
                    if (concepto && !compraDA) void runPreviewFor(r.id, concepto);
                  }}
                  onRemove={() =>
                    setRetenciones((prev) => prev.filter((x) => x.id !== r.id))
                  }
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Instrumentos de Pago */}
      <SectionHeader
        title="Instrumentos de Pago"
        onAdd={() =>
          setInstrumentos((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              tipo: '',
              medio: null,
              importe: '',
              descripcion: '',
            },
          ])
        }
      />
      {instrumentos.length === 0 ? (
        <EmptyNote
          icon={<ReceiptText className="h-5 w-5" aria-hidden />}
          title="Aun no se agregaron instrumentos de pago"
          hint="Agrega un instrumento de pago para visualizarlo aquí"
        />
      ) : (
        <div className="space-y-2">
          {instrumentos.map((i, idx) => (
            <InstrumentoRow
              key={i.id}
              row={i}
              medios={payableMedios}
              mediosLoading={mediosQ.isPending}
              moneda={compraDisplayMoneda}
              tipoCambio={tcNum}
              llevaFactura={header?.LlevaFactura_CO ?? null}
              onSizeError={setFileSizeError}
              onChange={(patch) =>
                setInstrumentos((prev) =>
                  prev.map((r, ri) => (ri === idx ? { ...r, ...patch } : r)),
                )
              }
              onRemove={() =>
                setInstrumentos((prev) => prev.filter((_, ri) => ri !== idx))
              }
            />
          ))}
        </div>
      )}

      {/* QA #21: surface the "exceeds saldo" condition with a clear
          message above the footer instead of relying on the disabled
          button + yellow tint alone. Mirrors the legacy
          `lbl_AdvertenciaMonto_CO`. */}
      {exceedsSaldo && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">
          El importe ingresado supera el saldo restante.
        </div>
      )}

      {/* Footer */}
      <div className="sticky bottom-0 mt-2 flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-2xl border bg-card px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <TotalPill label="Saldo restante">
            {formatMoney(saldoRestante, compraDisplayMoneda)}
          </TotalPill>
          <TotalPill label="Instrumentos">
            {formatMoney(sumInstrumentos, compraDisplayMoneda)}
          </TotalPill>
          {/* On USD compras the Importe is in USD; this is the pesos that
              actually leave the account (Σ instrumentos × tipo de cambio). */}
          {compraMoneda === 'Dolares' && tcNum > 0 && (
            <TotalPill label="Instrumentos (ARS)">
              ≈ {formatMoney(sumInstrumentos * tcNum, 'ARS')}
            </TotalPill>
          )}
          {/* Retenciones pill is always rendered so the footer columns
              don't shift when the admin adds the first retención. The
              `(ARS)` suffix only appears on USD compras since the row
              importes themselves are pesos-denominated. */}
          <TotalPill label={`Retenciones${compraMoneda === 'Dolares' ? ' (ARS)' : ''}`}>
            {formatMoney(sumRetenidoPesos, 'ARS')}
          </TotalPill>
          {ncPendientesSum > 0 && (
            <TotalPill label={`NC aplicadas (${ncPendientesCount})`}>
              −{formatMoney(ncPendientesSum, compraDisplayMoneda)}
            </TotalPill>
          )}
          {leyPreview.total > 0 && (
            <TotalPill label="Ley 25.413 (0,6%)">
              {formatMoney(leyPreview.total, 'ARS')}
            </TotalPill>
          )}
          <TotalPill label="Total pago" emphasize={fullyCovered} warn={exceedsSaldo}>
            {formatMoney(totalPago, compraDisplayMoneda)}
          </TotalPill>
        </div>
        {!isReadOnly && (
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={!canSubmit}
            title={validationError ?? undefined}
            className="gap-2 bg-foreground px-5 text-background hover:bg-foreground/90"
          >
            Confirmar Pago
            <Stamp className="h-4 w-4" aria-hidden />
          </Button>
        )}
      </div>

      <ConfirmarPagoDialog
        open={confirmOpen}
        proveedor={header?.Proveedor_CO ?? ''}
        comprobante={header?.NumeroComprobante_CO ?? ''}
        moneda={compraDisplayMoneda}
        totalPago={totalPago}
        sumInstrumentos={sumInstrumentos}
        sumRetenidoPesos={sumRetenidoPesos}
        compraMoneda={compraMoneda}
        saldoRestanteAntes={saldoPrevio}
        saldoRestanteDespues={saldoRestante}
        fullyCovered={fullyCovered}
        leyPreview={leyPreview}
        pending={submitMutation.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          submitMutation.mutate();
        }}
      />

      <ErrorDialog
        open={fileSizeError !== null}
        message={fileSizeError ?? ''}
        title="Archivo demasiado grande"
        onClose={() => setFileSizeError(null)}
      />
      {submitMutation.isPending && <ProcessingOverlay message="Confirmando pago…" />}
    </div>
  );
}

/**
 * Pre-submit confirmation. Surfaces the key figures (total pago,
 * instrumentos, retenciones, nuevo saldo) so the admin can catch a
 * misclick before the backend writes the pago. Visually mirrors
 * `ConfirmarEliminarDialog` (rose icon badge + summary body + primary
 * action) to stay consistent with other confirmation flows.
 */
function ConfirmarPagoDialog({
  open,
  proveedor,
  comprobante,
  moneda,
  totalPago,
  sumInstrumentos,
  sumRetenidoPesos,
  compraMoneda,
  saldoRestanteAntes,
  saldoRestanteDespues,
  fullyCovered,
  leyPreview,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  proveedor: string;
  comprobante: string;
  moneda: 'ARS' | 'MXN' | 'USD';
  totalPago: number;
  sumInstrumentos: number;
  sumRetenidoPesos: number;
  compraMoneda: 'Pesos' | 'Dolares';
  saldoRestanteAntes: number;
  saldoRestanteDespues: number;
  fullyCovered: boolean;
  leyPreview: { total: number; count: number };
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && !pending && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <Stamp className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>Confirmar pago</DialogTitle>
              <DialogDescription>
                Vas a registrar un pago para{' '}
                <span className="font-medium text-foreground">{proveedor}</span>
                {comprobante ? (
                  <>
                    {' '}(Comprobante{' '}
                    <span className="text-foreground">{comprobante}</span>)
                  </>
                ) : null}
                .
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-3 text-sm">
          <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5">
            <dt className="text-muted-foreground">Total pago</dt>
            <dd className="text-right font-semibold text-foreground">
              {formatMoney(totalPago, moneda)}
            </dd>
            <dt className="text-muted-foreground">Instrumentos</dt>
            <dd className="text-right text-foreground">
              {formatMoney(sumInstrumentos, moneda)}
            </dd>
            {sumRetenidoPesos > 0 && (
              <>
                <dt className="text-muted-foreground">
                  Retenciones{compraMoneda === 'Dolares' ? ' (ARS)' : ''}
                </dt>
                <dd className="text-right text-foreground">
                  {formatMoney(sumRetenidoPesos, 'ARS')}
                </dd>
              </>
            )}
            {leyPreview.total > 0 && (
              <>
                <dt className="text-muted-foreground">Ley 25.413 (0,6%)</dt>
                <dd className="text-right text-foreground">
                  {formatMoney(leyPreview.total, 'ARS')}
                </dd>
              </>
            )}
            <dt className="text-muted-foreground">Saldo antes</dt>
            <dd className="text-right text-foreground">
              {formatMoney(saldoRestanteAntes, moneda)}
            </dd>
            <dt className="text-muted-foreground">Saldo restante</dt>
            <dd
              className={cn(
                'text-right font-semibold',
                fullyCovered ? 'text-emerald-700' : 'text-amber-700',
              )}
            >
              {formatMoney(saldoRestanteDespues, moneda)}
            </dd>
          </dl>
          <p className="text-xs text-muted-foreground">
            {fullyCovered
              ? 'Este pago cubre el saldo completo. La compra quedará marcada como Pagada.'
              : 'Este es un pago parcial. La compra quedará en estado Pago Parcial.'}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            Volver
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="gap-2 bg-foreground text-background hover:bg-foreground/90"
          >
            Confirmar Pago
            <Stamp className="h-4 w-4" aria-hidden />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/** 4 MB cap — matches FileUploader/Vercel serverless body limit. */
const INSTRUMENTO_MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

function InstrumentoRow({
  row,
  medios,
  mediosLoading,
  moneda,
  tipoCambio,
  llevaFactura,
  onChange,
  onRemove,
  onSizeError,
}: {
  row: InstrumentoDraft;
  medios: MedioDePagoItem[];
  mediosLoading: boolean;
  moneda: 'ARS' | 'MXN' | 'USD';
  /** Compra tipo de cambio (USD→ARS). >0 only on USD compras with a TC set. */
  tipoCambio: number;
  /**
   * `LlevaFactura_CO` from the compra header (`'SI'` | `'NO'` | null).
   * Drives the Banco-tipo cuenta filter — Morba/Morvalab is exclusive
   * to LlevaFactura='NO' compras (PowerApps `Cbox_AgregarCuenta_PF.Items`,
   * Screen_PagarFactura.pa.yaml:2904-2934).
   */
  llevaFactura: string | null;
  onChange: (patch: Partial<InstrumentoDraft>) => void;
  onRemove: () => void;
  /** Surfaces "file too large" via the page-level ErrorDialog. */
  onSizeError: (msg: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  // Blob URL is regenerated whenever the file changes; revoked on
  // cleanup so picking a new file or clearing doesn't leak memory.
  const fileBlobUrl = useMemo(
    () => (row.pendingFile ? URL.createObjectURL(row.pendingFile) : null),
    [row.pendingFile],
  );
  useEffect(() => {
    if (!fileBlobUrl) return;
    return () => URL.revokeObjectURL(fileBlobUrl);
  }, [fileBlobUrl]);
  // Infer Tipo from Medio_MDP prefix so the cascade works against the
  // current data shape (Categoria_MDP is Local/Internacional, not
  // Banco/Efectivo). Matches the PowerApps hardcoded Tipo options.
  const filtered = useMemo(() => {
    if (!row.tipo) return [];
    const sameTipo = medios.filter(
      (m) => inferTipo(m.fields.Medio_MDP ?? '') === row.tipo,
    );
    if (row.tipo !== 'Banco') return sameTipo;
    // PowerApps splits the Banco list two ways:
    //   LlevaFactura='SI' → Banco Galicia / Galicia Dolar / MercadoPago / Credicoop
    //   LlevaFactura='NO' → Morba/Morvalab only (a black-market sleeve)
    // We don't carry display-name aliases in our catalog — match by
    // substring on Medio_MDP so naming variations ("Morba",
    // "Morvalab", "Transferencia Morba") all resolve correctly.
    const noFactura = (llevaFactura ?? '').trim().toUpperCase() === 'NO';
    return sameTipo.filter((m) => {
      const medio = (m.fields.Medio_MDP ?? '').toLowerCase();
      const morba = medio.includes('morba') || medio.includes('morvalab');
      return noFactura ? morba : !morba;
    });
  }, [medios, row.tipo, llevaFactura]);

  return (
    <div className="grid grid-cols-1 items-end gap-3 rounded-lg border bg-card p-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1.2fr)_auto]">
      <Field label="Tipo">
        <Select
          value={row.tipo}
          onValueChange={(v) => {
            onChange({ tipo: v as 'Banco' | 'Efectivo', medio: null });
          }}
          disabled={mediosLoading}
        >
          <SelectTrigger>
            <SelectValue placeholder={mediosLoading ? 'Cargando…' : 'Seleccionar…'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Banco">Banco</SelectItem>
            <SelectItem value="Efectivo">Efectivo</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Cuenta">
        <Combobox
          value={row.medio}
          onChange={(m) => onChange({ medio: m })}
          items={filtered}
          getKey={(m) => m.id}
          // Se muestra la caja REAL de la que sale la plata: el server asienta
          // el efectivo de una compra sin factura en "… N" con esta misma
          // regla (lib/cajasEfectivo), así que el admin lo ve antes de pagar.
          getLabel={(m) =>
            etiquetaCaja(
              cajaEfectivoPara(
                m.fields.Medio_MDP?.trim() || '—',
                (llevaFactura ?? '').trim().toUpperCase() === 'SI',
              ),
            )
          }
          matcher={(m, q) => (m.fields.Medio_MDP ?? '').toLowerCase().includes(q)}
          disabled={!row.tipo}
          placeholder={!row.tipo ? 'Elegí tipo' : 'Seleccionar…'}
          className="h-12"
        />
      </Field>
      <Field label="Importe">
        <MoneyInput
          variant="filled"
          placeholder="0,00"
          value={row.importe}
          moneda={moneda}
          onChange={(next) => onChange({ importe: next })}
        />
      </Field>
      <Field label="Descripción">
        <Input
          variant="filled"
          placeholder="Opcional"
          value={row.descripcion}
          onChange={(e) => onChange({ descripcion: e.target.value })}
        />
      </Field>
      {/* Comprobante slot — buffered file, uploaded after the pago is
          committed (we don't have an instrumento id until then). The
          PowerApps origin used a folder convention; we use the existing
          `/api/documents/upload` infra with `entityType='Instrumento'`. */}
      <Field label="Comprobante">
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/png,image/jpeg"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            if (!f) return;
            if (f.size > INSTRUMENTO_MAX_UPLOAD_BYTES) {
              onSizeError(
                `El archivo pesa ${(f.size / (1024 * 1024)).toFixed(1)} MB ` +
                  'y el tope permitido es 4 MB. Comprimí el PDF o la imagen ' +
                  'antes de volver a intentarlo.',
              );
              e.target.value = '';
              return;
            }
            onChange({ pendingFile: f });
          }}
        />
        {row.pendingFile ? (
          <div className="flex h-12 items-center gap-1 rounded-lg border border-transparent bg-secondary px-2">
            <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <span
              className="min-w-0 flex-1 truncate text-sm"
              title={row.pendingFile.name}
            >
              {row.pendingFile.name}
            </span>
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              aria-label="Vista previa"
              title="Vista previa"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
            >
              <Eye className="h-3.5 w-3.5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => {
                onChange({ pendingFile: null });
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              aria-label="Quitar archivo"
              title="Quitar archivo"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="h-12 w-full justify-center gap-2 border-transparent bg-secondary font-normal hover:bg-secondary/80"
          >
            <Upload className="h-4 w-4" aria-hidden />
            Adjuntar
          </Button>
        )}
      </Field>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Quitar instrumento"
        title="Quitar"
        // h-12 matches the surrounding Inputs / Combobox / Adjuntar
        // button so the trash button bottom sits on the same baseline
        // as every other field in the row.
        className="inline-flex h-12 w-12 items-center justify-center self-end rounded-md border border-destructive/30 bg-destructive/10 text-destructive transition-colors hover:bg-destructive/20"
      >
        <Trash2 className="h-4 w-4" aria-hidden />
      </button>
      {/* Peso equivalent preview — on USD compras the Importe is entered in
          USD (to match the saldo), and the pesos that actually leave the
          account = Importe × Tipo de Cambio. We surface it here so the
          operator sees the conversion before confirming. */}
      {moneda === 'USD' && tipoCambio > 0 && (parseMoney(row.importe) ?? 0) > 0 && (
        <div className="col-span-full text-[11px] text-muted-foreground">
          Equivalente en pesos:{' '}
          <span className="font-medium text-foreground">
            {formatMoney((parseMoney(row.importe) ?? 0) * tipoCambio, 'ARS')}
          </span>
        </div>
      )}

      {/* In-page preview dialog — avoids the popup-blocked / token
          issues of opening a blob URL in a new tab. Same pattern Nueva
          Compra uses for buffered factura previews. */}
      {previewOpen && row.pendingFile && fileBlobUrl && (
        <Dialog open onOpenChange={(o) => !o && setPreviewOpen(false)}>
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle className="truncate">{row.pendingFile.name}</DialogTitle>
              <DialogDescription className="sr-only">
                Vista previa del comprobante a adjuntar.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="p-0">
              {row.pendingFile.type.startsWith('image/') ? (
                <img
                  src={fileBlobUrl}
                  alt={row.pendingFile.name}
                  className="max-h-[70vh] w-full object-contain"
                />
              ) : row.pendingFile.type === 'application/pdf' ? (
                <iframe
                  title={row.pendingFile.name}
                  src={fileBlobUrl}
                  className="h-[70vh] w-full border-0"
                />
              ) : (
                <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
                  No se puede previsualizar este tipo de archivo.
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button type="button" onClick={() => setPreviewOpen(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function RetencionRow({
  row,
  catalog,
  catalogLoading,
  fechaPagoIso,
  onChange,
  onConceptoPicked,
  onRemove,
}: {
  row: RetencionDraft;
  catalog: RetencionItem[];
  catalogLoading: boolean;
  /** Fecha del pago (`yyyy-mm-dd`) — es la fecha de la retención, read-only. */
  fechaPagoIso: string;
  onChange: (patch: Partial<RetencionDraft>) => void;
  onConceptoPicked: (concepto: RetencionItem | null) => void;
  onRemove: () => void;
}) {
  const noCorresponde =
    !!row.previewPath && RETENCION_NO_CORRESPONDE_PATHS.has(row.previewPath);
  // Pre-loaded Pendiente rows were authored at compra-creation time: concepto
  // and nº stay frozen; the retenido is recalculated from the fecha de pago
  // and stays editable (a manual amount sticks). The trash button stays active so
  // the row can still be skipped on this pago without losing its Pendiente
  // status (removing here doesn't anular the SP row).
  const isPendiente = !!row.existingId;
  return (
    // `minmax(0,Nfr)` caps each track at its fr share — without it, a long
    // Concepto label (some regimes are full paragraphs) pushes the combobox
    // wider than 2fr and squeezes Importe ISAR / Retenido. The combobox's
    // internal `truncate` only engages once its container is width-bounded.
    <div
      className={cn(
        'grid grid-cols-1 items-end gap-3 rounded-lg border bg-card p-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]',
        noCorresponde && 'border-amber-300 bg-amber-50/40',
      )}
    >
      <Field
        // Cálculo badge sits inline with the label — rendering it here
        // (instead of as a <p> below the combobox) keeps row height
        // identical whether or not a preview has run.
        label={
          <span className="flex items-center gap-2">
            <span>Concepto (Régimen)</span>
            {row.existingId && (
              <span
                className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium normal-case tracking-normal text-amber-900"
                title="Esta retención fue cargada al crear la compra; el importe se recalcula con la fecha de pago y se confirma con este pago."
              >
                Pendiente
              </span>
            )}
            {row.previewPath && (
              <span
                className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium normal-case tracking-normal text-foreground"
                title={row.previewReason ?? undefined}
              >
                {RETENCION_PATH_LABELS[row.previewPath] ?? row.previewPath}
              </span>
            )}
          </span>
        }
      >
        <Combobox
          value={row.concepto}
          onChange={onConceptoPicked}
          items={catalog}
          getKey={(c) => c.id}
          getLabel={(c) => c.fields.ConcatCodigoRegimenConcepto_R?.trim() || '—'}
          getSublabel={(c) => c.fields.Concepto_R}
          matcher={(c, q) =>
            (c.fields.ConcatCodigoRegimenConcepto_R ?? '').toLowerCase().includes(q) ||
            (c.fields.Concepto_R ?? '').toLowerCase().includes(q) ||
            (c.fields.CodigoRegimen_R ?? '').toLowerCase().includes(q)
          }
          placeholder={catalogLoading ? 'Cargando…' : 'Seleccionar…'}
          className="h-12"
          disabled={isPendiente}
          // Hide the × clear affordance on Pendiente rows — the value
          // is locked so a clear would just leave the field empty
          // with no way to repopulate it.
          clearable={!isPendiente}
        />
      </Field>
      <Field label="Nº Comprobante">
        <Input
          variant="filled"
          value={row.nroComprobante}
          onChange={(e) => onChange({ nroComprobante: e.target.value })}
          readOnly={isPendiente}
          disabled={isPendiente}
        />
      </Field>
      {/* La retención se practica al pagar: su fecha es SIEMPRE la fecha de
          pago del header. Se muestra en espejo (read-only) para que quede a la
          vista en la fila; el server la estampa desde `fechaPago`. */}
      <Field label="Fecha (del pago)">
        <Input
          variant="filled"
          value={fechaPagoIso ? isoToAr(fechaPagoIso) : '—'}
          readOnly
          disabled
        />
      </Field>
      <Field label="Importe ISAR">
        <Input
          variant="filled"
          type="text"
          value={formatMoney(parseMoney(row.importeISAR) ?? 0, 'ARS')}
          readOnly
          disabled
        />
      </Field>
      <Field label="Retenido (ARS)">
        <MoneyInput
          variant="filled"
          // Locked at 0 when the engine flagged no-corresponde
          // (PowerApps `PopUp_NoRetencion`).
          value={noCorresponde ? '0' : row.importeRetenido}
          moneda="ARS"
          onChange={(next) => onChange({ importeRetenido: next })}
          placeholder={row.recalc === 'pending' ? 'Calculando…' : '0,00'}
          readOnly={noCorresponde}
          disabled={noCorresponde}
        />
      </Field>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Quitar retención"
        title="Quitar"
        // h-12 matches the surrounding Combobox / Input / DatePicker so
        // the trash button bottom sits on the same baseline as every
        // other field in the row.
        className="inline-flex h-12 w-12 items-center justify-center self-end rounded-md border border-destructive/30 bg-destructive/10 text-destructive transition-colors hover:bg-destructive/20"
      >
        <Trash2 className="h-4 w-4" aria-hidden />
      </button>
      {row.retenidoAlCargar != null &&
        row.previewPath &&
        !row.manual &&
        !noCorresponde &&
        Math.abs(row.retenidoAlCargar - (parseMoney(row.importeRetenido) ?? 0)) > 0.005 && (
        <div className="col-span-full rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
          <span className="font-medium">Recalculada al pagar.</span> Al cargar la compra
          daba {formatMoney(row.retenidoAlCargar, 'ARS')}; la retención se determina con lo
          pagado y retenido al proveedor en el mes hasta hoy.
        </div>
      )}
      {noCorresponde && (
        <div className="col-span-full rounded-md border border-amber-300 bg-amber-100/60 px-3 py-2 text-xs text-amber-900">
          <span className="font-medium">No corresponde retención.</span>{' '}
          {row.previewReason ??
            'El cálculo cae por debajo del mínimo configurado.'}{' '}
          Se guardará con descripción{' '}
          <span className="font-medium">“Sin retención”</span> e importe{' '}
          <span className="font-medium">$0</span>.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

function SectionHeader({ title, onAdd }: { title: string; onAdd: () => void }) {
  return (
    <div className="flex items-center gap-3">
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <Button
        type="button"
        size="sm"
        onClick={onAdd}
        className="gap-2 bg-foreground text-background hover:bg-foreground/90"
      >
        Agregar
        <Plus className="h-3.5 w-3.5" aria-hidden />
      </Button>
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

/**
 * Empty-state card — mirrors the pattern from Nueva Compra's `EmptyState`:
 * dashed rounded card + rose icon badge + title + hint. Keeping both
 * screens visually consistent makes the Pagar Factura flow feel like the
 * same app as the Compras side it came from.
 */
function EmptyNote({
  icon,
  title,
  hint,
}: {
  icon: React.ReactNode;
  title: string;
  hint: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-card/40 px-4 py-10 text-center">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 text-rose-600">
        {icon}
      </span>
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function TotalPill({
  label,
  children,
  emphasize,
  warn,
}: {
  label: string;
  children: React.ReactNode;
  emphasize?: boolean;
  warn?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-9 items-center gap-2 rounded-lg border px-3',
        emphasize
          ? 'border-emerald-300 bg-emerald-50'
          : warn
            ? 'border-amber-300 bg-amber-50'
            : 'bg-secondary',
      )}
    >
      <span className="text-muted-foreground">{label}</span>
      <span
        className={cn(
          'font-semibold',
          emphasize
            ? 'text-emerald-800'
            : warn
              ? 'text-amber-800'
              : 'text-foreground',
        )}
      >
        {children}
      </span>
      {emphasize && <Check className="h-3.5 w-3.5 text-emerald-700" aria-hidden />}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Utils
// ---------------------------------------------------------------------------

function sanitizeDecimal(value: string): string {
  const cleaned = value.replace(/[^\d.,]/g, '');
  const match = cleaned.match(/^\d*([.,]\d*)?/);
  return match ? match[0] : '';
}

function formatPlain(n: number): string {
  if (!Number.isFinite(n)) return '';
  return String(round2(n)).replace('.', ',');
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Map a Medio_MDP name to its (Banco | Efectivo) category, mirroring
 * the PowerApps hardcoded Tipo dropdown. Everything whose name starts
 * with "Efectivo" is Efectivo; everything else (Transferencia …,
 * Banco …, MercadoPago, etc.) is Banco.
 */
function inferTipo(medio: string): 'Banco' | 'Efectivo' {
  return medio.trim().toLowerCase().startsWith('efectivo') ? 'Efectivo' : 'Banco';
}

function isoToAr(s: string): string {
  if (!s) return '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
}
